import { childDataField } from '../components/Dashboard/dataFieldUtils'
import DashboardTableQueryState, { drawerRowListKeys, hasDetail } from './DashboardTableQueryState'

export const TABLE_FILTER_QUERY_PREFIX = 'filter.'
export const RESERVED_QUERY_KEYS = ['program_id']

export function withoutReservedKeys(params) {
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return {}
  }
  return Object.keys(params).reduce((result, key) => {
    if (!RESERVED_QUERY_KEYS.includes(key)) {
      result[key] = params[key]
    }
    return result
  }, {})
}

export default {
  mixins: [DashboardTableQueryState],
  data() {
    return {
      openRow: null,
      openIndependentDrawers: {},
      screenRenderKey: 0,
      screenRenderTargets: []
    }
  },
  computed: {
    drawerNode() {
      return this.baseScreen ? this.findDrawerNode(this.baseScreen) : null
    },
    currentScreen() {
      const replacement = this.drawerNode ? {
        ...this.drawerNode,
        props: {
          ...this.drawerNode.props,
          value: Boolean(this.openRow),
          title: this.openRow ? (this.openRow.detailTitle || this.drawerNode.props.title) : this.drawerNode.props.title,
          subtitle: this.openRow ? (this.openRow.detailSubtitle || this.drawerNode.props.subtitle) : this.drawerNode.props.subtitle
        },
        children: this.openRow ? (this.openRow.detailContent || []) : []
      } : null
      let replaced = false
      const transformNode = (node) => {
        if (!node || typeof node !== 'object') {
          return node
        }
        if (node.type === 'lazy_ref') {
          return node
        }
        if (replacement && node.type === 'detail_drawer') {
          if (replaced) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.warn(
                'DashboardIndependentDrawers: mais de um nó "detail_drawer" encontrado — só o primeiro fica interativo, os demais são ignorados.'
              )
            }
            return node
          }
          replaced = true
          return replacement
        }
        const clone = { ...node }
        const tableProps = this.applyNodeQueryState(node, transformNode)
        if (tableProps) {
          clone.props = tableProps
        }
        const effectiveProps = clone.props || node.props
        if (effectiveProps && effectiveProps.instance && this.screenRenderTargets.includes(effectiveProps.instance)) {
          clone.props = { ...effectiveProps, renderGeneration: this.screenRenderKey }
        }
        if (Array.isArray(node.sections)) {
          clone.sections = node.sections.map(transformNode)
        }
        if (Array.isArray(node.children)) {
          clone.children = node.children.map(transformNode)
        }
        if (node.type === 'accordion_drilldown' && node.props && Array.isArray(node.props.items)) {
          clone.props = {
            ...clone.props,
            items: node.props.items.map((item) => ({
              ...item,
              tabs: Array.isArray(item.tabs)
                ? item.tabs.map((tab) => ({
                  ...tab,
                  children: Array.isArray(tab.children) ? tab.children.map(transformNode) : tab.children
                }))
                : item.tabs
            }))
          }
        }
        return clone
      }
      return transformNode(this.baseScreen)
    },
    openIndependentDrawersList() {
      return Object.keys(this.openIndependentDrawers).map((id) => ({
        id,
        row: this.openIndependentDrawers[id]
      }))
    }
  },
  methods: {
    performFetch() {
      if (typeof this.fetchScreen === 'function') {
        return this.fetchScreen()
      }
      return undefined
    },
    onRowDetail(row) {
      if (row && row.detail && row.detail.type === 'navigate') {
        this.navigateFromDetail(row.detail.props || {})
        return
      }
      if (row && row.detail && row.detail.props && row.detail.props.id) {
        this.$set(this.openIndependentDrawers, row.detail.props.id, row)
        return
      }
      this.openRow = row
    },
    async navigateFromDetail({ view, instance: targetInstance, params: rawParams, tableFilters } = {}) {
      const params = withoutReservedKeys(rawParams)
      const filters = Array.isArray(tableFilters) ? tableFilters.filter((f) => f && f.instance && f.param) : []
      const currentView = this.$route && this.$route.params ? this.$route.params.view : undefined
      if (!view || view === currentView) {
        if (this.filterValues) {
          this.filterValues = { ...this.filterValues, ...params }
        }
        filters.forEach(({ instance, param, value }) => {
          const declared = (this._declaredTableFilterParams || {})[instance]
          if (process.env.NODE_ENV !== 'production' && declared && !declared.includes(param)) {
            // eslint-disable-next-line no-console
            console.warn(
              `DashboardIndependentDrawers: tableFilters do navigate usa "${param}", que a tabela "${instance}" não declara em "filters" — o usuário não consegue desfazer esse filtro; ele será descartado na próxima interação com os filtros da tabela.`
            )
          }
          this.setTableFilterValue(instance, param, value)
        })
        await this.performFetch()
        this.screenRenderTargets = [...new Set([...filters.map((f) => f.instance), targetInstance].filter(Boolean))]
        this.screenRenderKey += 1
        return
      }
      const query = { ...params }
      filters.forEach(({ instance, param, value }) => {
        query[`${TABLE_FILTER_QUERY_PREFIX}${instance}.${param}`] = value
      })
      this.$router.push({ path: `/screens/${view}`, query }).catch(() => {})
    },
    onDrawerInput(value) {
      if (!value) {
        this.openRow = null
      }
    },
    onIndependentDrawerInput(id, value) {
      if (!value) {
        this.$delete(this.openIndependentDrawers, id)
      }
    },
    findDrawerNode(node) {
      if (!node || typeof node !== 'object') {
        return null
      }
      if (node.type === 'detail_drawer') {
        return node
      }
      const lists = [node.sections, node.children].filter(Array.isArray)
      if (node.type === 'accordion_drilldown' && node.props && Array.isArray(node.props.items)) {
        node.props.items.forEach((item) => {
          if (Array.isArray(item.tabs)) {
            item.tabs.forEach((tab) => {
              if (Array.isArray(tab.children)) {
                lists.push(tab.children)
              }
            })
          }
        })
      }
      for (const list of lists) {
        for (const item of list) {
          const found = this.findDrawerNode(item)
          if (found) {
            return found
          }
        }
      }
      return null
    },
    findRowById(node, id) {
      if (!node || typeof node !== 'object') {
        return null
      }
      if (node.props && node.props.detail && node.props.detail.props && node.props.detail.props.id === id) {
        return { detail: node.props.detail }
      }
      const lists = [node.sections, node.children].filter(Array.isArray)
      if (node.props && node.props.detail && Array.isArray(node.props.detail.children)) {
        lists.push(node.props.detail.children)
      }
      for (const key of drawerRowListKeys(node.props, hasDetail)) {
        for (const row of node.props[key]) {
          if (row && row.detail && row.detail.props && row.detail.props.id === id) {
            return row
          }
          if (row && row.detail && Array.isArray(row.detail.children)) {
            lists.push(row.detail.children)
          }
        }
      }
      if (node.type === 'accordion_drilldown' && node.props && Array.isArray(node.props.items)) {
        node.props.items.forEach((item) => {
          if (Array.isArray(item.tabs)) {
            item.tabs.forEach((tab) => {
              if (Array.isArray(tab.children)) {
                lists.push(tab.children)
              }
            })
          }
        })
      }
      for (const list of lists) {
        for (const item of list) {
          const found = this.findRowById(item, id)
          if (found) {
            return found
          }
        }
      }
      return null
    },
    resyncOpenDrawers() {
      Object.keys(this.openIndependentDrawers).forEach((id) => {
        const freshRow = this.findRowById(this.currentScreen, id)
        if (freshRow) {
          this.$set(this.openIndependentDrawers, id, freshRow)
        }
      })
    },
    independentDrawerChildDataField(drawerNode, child, index) {
      return childDataField(drawerNode, child, index)
    }
  }
}
